import {
  BoxType,
  GroupPeopleType,
  type CustomGroupTemplateBox,
  type CustomImageTemplateBox,
  type CustomTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableGroupData } from '../interfaces/drawable-data.interface';
import { imageAlbumFromAssignedPhoto } from '../normalizer/boxes/assigned-photo';
import {
  adoptedPeopleTypeForOwnerPromotion,
  getSpreadGroups,
  isPersonalSpread,
  ownerPortraitFrameKind,
  spendsPortraitBudget,
  type OwnerPromotionGroupLike,
  type PersonalFrameParams,
} from './personal-spread-queries';
import {
  danglingRosterCellIds,
  isSeatableRosterCell,
  ownerAnchorGroupId,
  promotableOwnerAnchorGroupId,
  rosterCellPredicate,
  seatedPersonIds,
  type RosterCellLike,
  type RosterSurface,
} from './roster-seating-queries';

/**
 * v1 (custom-template-v1 boxes) вход в ростерные/личные мерки поверхности. ЗАКОН один и живёт на
 * drawable-стороне: `personal-spread-queries.ts` / `roster-seating-queries.ts` читают контейнер дуально
 * (`containedDrawables` — и `drawableObjects`, и `boxes`), поэтому здесь нет собственной логики — только
 * типизированные обёртки. Второй мерки заводить нельзя: разойдись они, редактор и серверный гейт записи
 * дали бы разный ответ про одну и ту же поверхность.
 *
 * Исключений ДВА, и оба по одной причине — оригинал не читает контейнер дуально, а адресует его сам.
 * Портретный потолок (`ownerPortraitFrames`) ходит по `collage.drawableObjects` НАПРЯМУЮ, а промоушен
 * роли (`promoteGroupRoleToOwner`) в него ПИШЕТ. У обоих здесь настоящий v1-твин, а не обёртка. У
 * потолка второй экземпляр — только обход контейнера; у промоушена их два — обход И форма записи
 * `params` (полный список её экземпляров назван у {@link promoteGroupRoleToOwnerBoxes}). ЗАКОН
 * каждого при этом остаётся один и у своего оригинала.
 */

/** Разворот глазами рассадки в персист-форме: только состав боксов. */
export type SpreadBoxesLike = { readonly collages?: readonly { readonly boxes?: readonly CustomTemplateBox[] }[] | null };

/**
 * ЕДИНСТВЕННОЕ место каста форм в файле. Рантайм у сторон общий: дискриминант тот же строковый
 * (`BoxType.GROUP === 'group'`, `BoxType.IMAGE === 'image'`), а контейнер develop читает дуально.
 * Расходятся только ТИПЫ: `RosterSurface` требует `CollageData`, у которого `drawableObjects`
 * обязателен, а `PersistedCollageV1` его не несёт. Расширять `RosterSurface`/`SpreadLike` в общем
 * файле нельзя — правка перестала бы быть аддитивной для develop.
 */
function asDrawableSurface(spread: SpreadBoxesLike): RosterSurface {
  return spread as unknown as RosterSurface;
}

/** Обёртка getSpreadGroups: верхнеуровневые группы поверхности. */
export function getSpreadGroupBoxes(spread: SpreadBoxesLike): CustomGroupTemplateBox[] {
  return getSpreadGroups(asDrawableSurface(spread)) as unknown as CustomGroupTemplateBox[];
}

/** Обёртка isPersonalSpread: есть ли на развороте рамка владельца (портрет или «я с группой»). */
export function isPersonalSpreadBoxes(spread: SpreadBoxesLike | null | undefined): boolean {
  return !!spread && isPersonalSpread(asDrawableSurface(spread));
}

/**
 * Обёртка rosterCellPredicate: та же мерка ячейки, что у гидрации и вырезания, на форме боксов.
 * Голый якорь эта поверхность отдаёт ПАРОЙ и ниже по файлу (`ownerAnchorGroupIdBox` /
 * `promotableOwnerAnchorGroupIdBox`): в ядре мерок якоря ДВЕ, и слабая из них писателю роли запрещена —
 * взяв её на вложенной сетке, он необратимо переписал бы роль одноклассника. Здесь якорь по-прежнему
 * не выдаётся: предикат ячейки его уже ВЫЧЕЛ, и отдавать наружу ещё и половину собственного ответа
 * значило бы предложить вызывающему вычесть его второй раз.
 */
export function rosterCellPredicateBox(
  surface: SpreadBoxesLike,
  rosterSurface: boolean,
): (group: CustomGroupTemplateBox) => boolean {
  const isCell = rosterCellPredicate(asDrawableSurface(surface), rosterSurface);
  return group => isCell(group as unknown as RosterCellLike);
}

/**
 * Обёртка danglingRosterCellIds: id ячеек, посаженных на персону, которой в проекте больше НЕТ.
 * Собственной логики здесь нет и быть не должно — закон живёт в ядре; разойдись мерки, редактор
 * пометил бы бейджем не ту рамку, которую освобождает генератор.
 */
export function danglingRosterCellIdsBoxes(
  surface: SpreadBoxesLike,
  rosterSurface: boolean,
  alivePersonIds: ReadonlySet<string>,
): string[] {
  return danglingRosterCellIds(asDrawableSurface(surface), rosterSurface, alivePersonIds);
}

/** Обёртка seatedPersonIds: занятые на ВСЕЙ поверхности — виньетка делит класс между страницами. */
export function seatedPersonIdsBoxes(surface: SpreadBoxesLike): ReadonlySet<string> {
  return seatedPersonIds(asDrawableSurface(surface));
}

/** Обёртка isSeatableRosterCell: ростерная роль минус рамка владельца. */
export function isSeatableRosterCellBox(surface: SpreadBoxesLike, group: CustomGroupTemplateBox | null | undefined): boolean {
  return isSeatableRosterCell(asDrawableSurface(surface), (group ?? null) as unknown as DrawableGroupData | null);
}

/** Книга глазами портретного потолка в персист-форме: только развороты — обложка в потолок не входит. */
type BookBoxesLike = { readonly spreads?: readonly SpreadBoxesLike[] | null };

/**
 * v1-твин `eachSpreadImageFrame`: обход рамок разворотов ПО ВСЕЙ ГЛУБИНЕ, род несёт БЛИЖАЙШАЯ группа со
 * своими `params`. Обложка вне обхода — потолок меряется только разворотами.
 */
function eachSpreadImageFrameBox(
  book: BookBoxesLike | null | undefined,
  visit: (frame: CustomImageTemplateBox, params: PersonalFrameParams | undefined) => void,
): void {
  const walk = (boxes: readonly CustomTemplateBox[], params: PersonalFrameParams | undefined): void => {
    for (const box of boxes) {
      if (box.type === BoxType.GROUP) {
        walk(box.boxes ?? [], box.params ?? params);
        continue;
      }

      if (box.type === BoxType.IMAGE) visit(box, params);
    }
  };

  for (const spread of book?.spreads ?? []) {
    for (const collage of spread.collages ?? []) walk(collage.boxes ?? [], undefined);
  }
}

/**
 * v1-твин `ownerPortraitFrames`: ЁМКОСТЬ потолка — рамки, в которые свой портрет ученика встать МОЖЕТ
 * ФИЗИЧЕСКИ (`ownerPortraitFrameKind(...) !== false`): тип «Мой портрет», рамка владельческой группы И
 * безродная рамка; вне потолка только рамка, названная ЧУЖИМ родом. Судит тот же предикат, что и
 * drawable-сторона, — разойдись мерки, редактор ученика и серверный гейт записи дали бы разный ответ про
 * один и тот же альбом. Твин, а не обёртка: develop-оригинал ходит по `collage.drawableObjects` напрямую
 * и v1-контейнер не видит.
 */
export function ownerPortraitFramesBoxes(book: BookBoxesLike | null | undefined): CustomImageTemplateBox[] {
  const frames: CustomImageTemplateBox[] = [];
  eachSpreadImageFrameBox(book, (frame, params) => {
    if (ownerPortraitFrameKind(frame.params.frameRole, params) !== false) frames.push(frame);
  });

  return frames;
}

/**
 * v1-твин `countUsedOwnerPortraitFrames`: сколько рамок потолка уже ЗАНЯТО — те же рамки, в которых стоит
 * ОДИНОЧНЫЙ портрет владельца (`spendsPortraitBudget`). Ёмкость ⊇ расход по построению: обе стороны
 * спрашивают один предикат. Кадр читается ЕДИНСТВЕННОЙ конверсией `assignedPhoto`↔`ImageAlbum`, второй
 * таблицы соответствий нет.
 */
export function countUsedOwnerPortraitFramesBoxes(book: BookBoxesLike | null | undefined, ownerPersonId?: string | null): number {
  let used = 0;
  eachSpreadImageFrameBox(book, (frame, params) => {
    const image = frame.params.assignedPhoto ? imageAlbumFromAssignedPhoto(frame.params.assignedPhoto) : null;
    if (spendsPortraitBudget({ frameRole: frame.params.frameRole, image }, params, ownerPersonId)) used++;
  });

  return used;
}

/**
 * Якорь, которому ПОЗВОЛЕНА необратимая запись роли. Брендирован, а не гол: писатель роли
 * ({@link promoteGroupRoleToOwnerBoxes}) принимает только его, поэтому слабая мерка
 * ({@link ownerAnchorGroupIdBox}) в промоушен не пролезет — это ошибка КОМПИЛЯЦИИ, а не недочитанный
 * комментарий. Единственный производитель бренда — {@link promotableOwnerAnchorGroupIdBox}.
 */
export type PromotableAnchorGroupId = string & { readonly __promotableAnchor: 'roster-nesting-checked' };

/**
 * Обёртки ЯКОРЯ владельца — ПАРОЙ и только парой. Мерок якоря в ядре две, и разница между ними не
 * стилистическая: слабая (`ownerAnchorGroupId`) годится для ЧТЕНИЯ — её ответ обратим и на следующем
 * проходе пересчитывается; сильная (`promotableOwnerAnchorGroupId`) обязательна ПИСАТЕЛЮ роли, потому
 * что она добавляет охранник вложенной ростерной сетки, а запись роли необратима. Выставить наружу
 * одну слабую значило бы отдать писателю ровно тот инструмент, которым он необратимо переписал бы
 * роль одноклассника; выставить одну сильную — сузить чтение и вычесть из освобождения якорь там, где
 * он есть. Поэтому обе стоят рядом — и разведены ТИПОМ, а не только этим текстом: слабая отдаёт голую
 * строку, сильная — {@link PromotableAnchorGroupId}, и только он принимается писателем.
 */
export function ownerAnchorGroupIdBox(surface: SpreadBoxesLike, rosterSurface: boolean): string | null {
  return ownerAnchorGroupId(asDrawableSurface(surface), rosterSurface);
}

/**
 * Пара к {@link ownerAnchorGroupIdBox}: мерка, которой ПОЗВОЛЕНО переписать роль группы в разметке.
 * ЕДИНСТВЕННОЕ место, где надевается бренд, — здесь: он и означает «охранник вложенной ростерной
 * сетки пройден», а пройден он ровно этой меркой ядра.
 */
export function promotableOwnerAnchorGroupIdBox(
  surface: SpreadBoxesLike,
  rosterSurface: boolean,
): PromotableAnchorGroupId | null {
  return promotableOwnerAnchorGroupId(asDrawableSurface(surface), rosterSurface) as PromotableAnchorGroupId | null;
}

/**
 * v1-твин {@link promoteGroupRoleToOwner}, а не обёртка: оригинал ПИШЕТ в `drawableObjects` и боксовый
 * контейнер не видит вовсе. Удвоено здесь ДВА: обход контейнера и ФОРМА ЗАПИСИ `params` промоушена
 * (какое поле ставим, какое дописываем условно). Формы записи в репозитории теперь три, и это полный
 * список: `personal-spread-queries.ts:166` (v0-оригинал), эта функция и
 * `apps/backend/core-service/src/scripts/migrate-owner-frame-role.pure.ts:352` (скрипт миграции) —
 * добавь кто-нибудь четвёртое поле в запись, чинить надо все три. ЗАКОН «какой род принимает рамка,
 * становясь владельческой» не повторяется ни строкой и остаётся один, у
 * `adoptedPeopleTypeForOwnerPromotion`: разойдись он, мигрированная и свежесобранная книга дали бы
 * ученику разный набор доступных ему кадров.
 *
 * Кадр рамки приезжает в закон ЕДИНСТВЕННОЙ конверсией `assignedPhoto`↔`ImageAlbum` — той же, что
 * зовёт кодек; собственной таблицы соответствий у твина нет.
 *
 * `groupId` брендирован: роль пишется необратимо, поэтому взять сюда слабую мерку якоря нельзя —
 * см. {@link PromotableAnchorGroupId}.
 */
export function promoteGroupRoleToOwnerBoxes<T extends { readonly boxes: readonly CustomTemplateBox[] }>(
  collage: T,
  groupId: PromotableAnchorGroupId,
  ownerPersonId?: string | null,
): T {
  let changed = false;
  const boxes = collage.boxes.map((box): CustomTemplateBox => {
    if (box.type !== BoxType.GROUP || box.id !== groupId) return box;
    if (box.params?.peopleType === GroupPeopleType.OWNER) return box;
    changed = true;
    const adoptedPeopleType = adoptedPeopleTypeForOwnerPromotion(asImageBearingGroup(box), ownerPersonId);

    return {
      ...box,
      params: { ...box.params, peopleType: GroupPeopleType.OWNER, ...(adoptedPeopleType && { adoptedPeopleType }) },
    };
  });

  return changed ? { ...collage, boxes } : collage;
}

/**
 * Группа глазами закона о принятом роде: её `params` плюс её ФОТОРАМКИ с уже разобранным кадром.
 * Глубина — прямые дети, как у оригинала (`group.drawableObjects.filter(isDrawableImage)`), а не
 * рекурсия: род принимает рамка самой группы, а не рамка вложенной. Канонические плоские обходы
 * кадров (`getDrawableImagesBoxes`, `collage-queries.ts:82`) взять НЕЛЬЗЯ по типу входа: они
 * типизированы на `PersistedCollageV1`, то есть на КОЛЛАЖ, а здесь контейнер — ГРУППА; фильтр и
 * глубина у них те же, поэтому второй мерки «что такое кадр» тут не появляется.
 *
 * Каста нет и быть не должно: вход закона объявлен УЗКИМ ({@link OwnerPromotionGroupLike} — ровно
 * `params.adoptedPeopleType`, `type` и `image`), поэтому боксовая форма садится в него структурно, а
 * новое ОБЯЗАТЕЛЬНОЕ поле у закона сломает СБОРКУ здесь, а не данные у ученика; опциональное сборку не
 * покрасит — почему так и что даёт узкий вход взамен, названо у самого типа.
 */
function asImageBearingGroup(group: CustomGroupTemplateBox): OwnerPromotionGroupLike {
  const drawableObjects = (group.boxes ?? [])
    .filter((child): child is CustomImageTemplateBox => child.type === BoxType.IMAGE)
    .map(child => ({
      type: child.type,
      image: child.params.assignedPhoto ? imageAlbumFromAssignedPhoto(child.params.assignedPhoto) : null,
    }));

  return { params: group.params, drawableObjects };
}
